'use client';

import { useState, useEffect } from 'react';
import { useCollections, Collection } from './CollectionsContext';
import { Button } from '@/components/ui/button';
import { MediaSelector } from '@/components/ui/media-selector';
import { useSession } from '@/lib/auth-client';
import clsx from 'clsx';

interface CollectionSettingsProps {
  collection: Collection;
  onSave?: (updatedCollection: Collection) => void;
  onCancel?: () => void;
  onDelete?: () => void;
}

export function CollectionSettings({
  collection,
  onSave,
  onCancel,
  onDelete,
}: CollectionSettingsProps) {
  const { updateCollection } = useCollections();
  const { data: session } = useSession();
  const isAdmin = !!(session?.user as any)?.isAdmin;
  const [name, setName] = useState(collection.name);
  const [description, setDescription] = useState(collection.description || '');
  const [image, setImage] = useState<string | null>(collection.image || null);
  const [visibility, setVisibility] = useState<'public' | 'private'>(collection.visibility);
  const [tagsInput, setTagsInput] = useState((collection.tags || []).join(', '));
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when collection changes
  useEffect(() => {
    setName(collection.name);
    setDescription(collection.description || '');
    setImage(collection.image || null);
    setVisibility(collection.visibility);
    setTagsInput((collection.tags || []).join(', '));
  }, [collection]);

  const handleSubmit = async () => {
    if (name.trim()) {
      setIsSaving(true);
      try {
        const parsedTags = tagsInput
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);

        const updates: any = {
          name: name.trim(),
          description: description.trim() || undefined,
          image,
          visibility,
        };

        // Only include tags if admin
        if (isAdmin) {
          updates.tags = parsedTags;
        }

        updateCollection(collection.id, updates);

        // Create updated collection object for immediate UI update
        const updatedCollection: Collection = {
          ...collection,
          ...updates,
        };

        // Call the onSave callback with the updated collection
        onSave?.(updatedCollection);
      } catch (error) {
        console.error('Failed to update collection:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="space-y-5">
      {/* Collection Image */}
      <MediaSelector
        value={image}
        onChange={(url) => setImage(typeof url === 'string' ? url : url?.[0] || null)}
        label="Collection Image"
        helperText="JPG, PNG or GIF. Max 5MB."
        size="lg"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Collection Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Healthy Snacks"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          autoFocus
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description (Optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a description for this collection..."
          rows={3}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
        />
      </div>

      {isAdmin && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tags (Admin only)</label>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="e.g. New York, CPG, Food & Beverage"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
          <p className="text-xs text-gray-500 mt-1">Comma-separated list of tags</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setVisibility('private')}
            className={clsx(
              'flex-1 px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2',
              visibility === 'private'
                ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                : 'border-gray-100 hover:border-gray-200 text-gray-500'
            )}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            Private
          </button>
          <button
            type="button"
            onClick={() => setVisibility('public')}
            className={clsx(
              'flex-1 px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2',
              visibility === 'public'
                ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                : 'border-gray-100 hover:border-gray-200 text-gray-500'
            )}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Public
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 ml-1">
          {visibility === 'private'
            ? 'Only you can see this collection'
            : 'Anyone with the link can view this collection'}
        </p>
      </div>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!name.trim() || isSaving} className="flex-[2]">
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {onDelete && (
        <div className="pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onDelete}
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Delete Collection
          </Button>
        </div>
      )}
    </div>
  );
}
